import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { type ReactNode, useRef, useState } from 'react';

import Button from '@/componentsv2/Button';
import Checkbox from '@/componentsv2/Checkbox';
import { Drawer } from '@/componentsv2/Drawer';
import Input from '@/componentsv2/Input';
import { ERROR_CODES, firstErrorCode, hasOnlyErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import ConnectGateBanner from '../components/ExpertMakeOfferDrawer/ConnectGateBanner';
import RateNudge, { rateNudgeVariantFor } from '../components/ExpertMakeOfferDrawer/RateNudge';
import { isPayoutReady } from '../components/shared/connect';
import OfferTerms, {
  type OfferTermsValue,
  initialOfferTerms,
  offerTermsErrors,
  offerTermsInput,
} from './OfferTerms';
import { connectSetupError } from './connectSetupError';
import {
  DISMISS_ENGAGEMENT_RATE_NUDGE,
  ENGAGEMENT_EXPERT_PROFILE_RATE,
  ENGAGEMENT_PAYOUT_ACCOUNT,
  ENGAGEMENT_PAYOUT_ONBOARDING_URL,
  MAKE_ENGAGEMENT_OFFER,
} from './operations';
import type { EngagementView } from './viewModel';

/** Referenced by whichever gate button is rendered, per `HireExpertDrawer`. */
const GATE_ERROR_ID = 'make-offer-gate-error';

interface MakeOfferDrawerProps {
  open: boolean;
  onClose: () => void;
  view: EngagementView;
  /** The expert's own profile, for the rate prefill and the writeback target. */
  viewerUserId: string;
  viewerProfileId: string | null;
}

/**
 * Expert Make Offer, wired to `makeEngagementOffer`.
 *
 * Scope, dates, cadence and pacing are back: ENG-5604 (G6) added the write-side
 * inputs FE2a had nowhere to send, so the sections it removed return additively
 * as plan §9 Q4 said they would. See `OfferTerms`.
 *
 * **The §9.11 rate nudge triggers from `expertOfferContext`** (ENG-5604 G7) —
 * an expert-side field carrying hour thresholds and no money. FE2a could not
 * show the nudge at all: the thresholds lived on the customer's billing account,
 * reachable only through `primaryProject`, which the api denies to the expert.
 * The same field gives the minimum-hours check an expert-visible source, so a
 * size the server would reject is now caught before the round-trip. Dismissal
 * persists server-side via `dismissEngagementRateNudge`.
 */
export default function MakeOfferDrawer({
  open,
  onClose,
  view,
  viewerUserId,
  viewerProfileId,
}: MakeOfferDrawerProps): JSX.Element {
  const [rateDollars, setRateDollars] = useState('');
  const [hours, setHours] = useState('');
  const [alsoUpdateProfileRate, setAlsoUpdateProfileRate] = useState(false);
  const [terms, setTerms] = useState<OfferTermsValue>(() =>
    initialOfferTerms(view.requestScopeDescription)
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Shared by both gate buttons: whichever one the expert pressed reports here.
  // ReactNode rather than string because the country case has to be actionable
  // — telling an expert their country is missing without handing them the form
  // is the same dead end in nicer words (ENG-5723).
  const [gateError, setGateError] = useState<ReactNode>(null);
  const [retrying, setRetrying] = useState(false);
  const [payoutsBlockedByServer, setPayoutsBlockedByServer] = useState(false);
  const [touched, setTouched] = useState(false);
  // Optimistic: the mutation persists it, but the nudge should go on click.
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const rateFieldRef = useRef<HTMLDivElement>(null);

  const payout = useQuery(ENGAGEMENT_PAYOUT_ACCOUNT, {
    variables: { userId: viewerUserId },
    skip: !open,
    fetchPolicy: 'network-only',
  });
  useQuery(ENGAGEMENT_EXPERT_PROFILE_RATE, {
    variables: { profileId: viewerProfileId ?? '' },
    skip: !open || !viewerProfileId,
    onCompleted: (data) => {
      const cents = data.profile?.bill_rate;
      if (cents && !touched) setRateDollars(String(cents / 100));
    },
  });
  const [makeOffer, { loading: submitting }] = useMutation(MAKE_ENGAGEMENT_OFFER);
  const [createOnboardingUrl, { loading: onboarding }] = useMutation(
    ENGAGEMENT_PAYOUT_ONBOARDING_URL
  );
  const [dismissRateNudge] = useMutation(DISMISS_ENGAGEMENT_RATE_NUDGE);

  // Fail closed, in this order: a read still in flight is not yet "ready", a
  // failed read is not "ready", and an absent account is not "ready" (plan
  // §4.2). Only a positive `payouts_enabled` opens the gate.
  const payoutReady =
    !payout.loading && !payout.error && isPayoutReady(payout.data?.payoutAccountByUserID);
  const blocked = payoutsBlockedByServer || !payoutReady;

  // ENG-5708. "No payout account" does not arrive as `null` — the api maps
  // `db.ErrNoMoreRows` to the coded validation error `payout.account_not_found`
  // (`payout/payout_service.go`), so an expert who has never onboarded reaches
  // us as a query ERROR. Reading every error as a failed read sent exactly that
  // expert to "Check again", whose retry re-raises the same deterministic error
  // forever, while the button that would create the account (the onboarding
  // mutation calls `ensureAccount`) stayed hidden behind an account existing.
  //
  // `hasOnlyErrorCode` and not `hasErrorCode`: a network error, or a sibling
  // error we did not expect, must still read as a failure worth retrying.
  const payoutAccountMissing = hasOnlyErrorCode(payout.error, ERROR_CODES.PAYOUT_ACCOUNT_NOT_FOUND);
  const payoutReadFailed = payout.error !== undefined && !payoutAccountMissing;
  // The read has come back one way or the other. Not `!payout.loading`: that is
  // also true before the query starts (`skip: !open`) and stays true through a
  // refetch, so it answers "is a request in flight", not "do we know yet".
  const payoutAnswered = payout.data !== undefined || payout.error !== undefined;

  const rateCents = Math.round(Number.parseFloat(rateDollars) * 100);
  const hoursValue = Number.parseFloat(hours);
  const offerContext = view.expertOfferContext;
  const rateError =
    !Number.isFinite(rateCents) || rateCents <= 0 ? 'Enter your hourly rate.' : null;
  // The account minimum is expert-visible now (G7), so a size the server would
  // reject is caught before the round-trip rather than mapped from
  // `engagement.nte_hours_below_minimum` afterwards.
  const hoursError =
    !Number.isFinite(hoursValue) || hoursValue <= 0
      ? 'Enter the not-to-exceed hours.'
      : offerContext !== null && hoursValue < offerContext.engagementMinHours
        ? `This account has a ${offerContext.engagementMinHours}-hour minimum.`
        : null;

  /**
   * §9.11 — fires on the live hours value, so it appears and disappears as the
   * expert types. Hidden once dismissed, on this device or any other: the
   * dismissal is a server field, not session state.
   */
  const nudgeVariant =
    offerContext === null || nudgeDismissed || view.rateNudgeDismissed === true
      ? null
      : rateNudgeVariantFor(
          Number.isFinite(hoursValue) ? hoursValue : null,
          offerContext.reducedThresholdHours,
          offerContext.longTermThresholdHours
        );

  function handleDismissNudge() {
    setNudgeDismissed(true);
    // Fire-and-forget: a failed persist costs the expert one re-dismissal on
    // their next visit, which is not worth an error banner over.
    void dismissRateNudge({
      variables: { input: { engagementId: view.id } },
      // The mutation returns a bare `Boolean!`, so Apollo has nothing to
      // normalize: the cached `Engagement.rateNudgeDismissed` would stay false
      // and the nudge would return the next time this drawer mounts, since
      // `nudgeDismissed` above is per-mount. Write the read side by hand.
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'Engagement', id: view.id }),
          fields: { rateNudgeDismissed: () => true },
        });
      },
    }).catch((err: unknown) => {
      // Still fire-and-forget for the expert, but not silent: a persist that
      // fails repeatedly is how a dismissal quietly stops surviving a reload,
      // and nothing else would ever surface that. `level: 'warning'` follows
      // `core/stripe.ts` — a known, already-handled condition, not an app bug.
      Sentry.captureException(err, {
        level: 'warning',
        tags: { engagement_rate_nudge_dismiss_failed: 'true' },
      });
    });
  }
  const termsErrors = offerTermsErrors(terms);
  const termsValid = Object.keys(termsErrors).length === 0;
  const canSubmit = !rateError && !hoursError && termsValid && !blocked && !submitting;

  async function handleSubmit() {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      await makeOffer({
        variables: {
          input: {
            engagementId: view.id,
            expertRate: { cents: String(rateCents), currency: 'USD' },
            nteHours: hoursValue.toFixed(2),
            alsoUpdateProfileRate,
            ...offerTermsInput(terms),
          },
        },
      });
      onClose();
    } catch (err) {
      const code = firstErrorCode(err);
      // The submit-race backstop: the gate passed on open but the account
      // lapsed (or was never really enabled) by the time we submitted.
      if (code === ERROR_CODES.ENGAGEMENT_EXPERT_PAYOUTS_NOT_ENABLED) {
        setPayoutsBlockedByServer(true);
      }
      setSubmitError(getDefaultErrorMessage(code ?? '') ?? 'Your offer could not be sent.');
    }
  }

  async function handleConnectSetup() {
    // The button reports pending through `loading` rather than `disabled`, so
    // it stays focusable and stays clickable: the guard is here instead.
    if (onboarding) return;
    setGateError(null);
    try {
      const { data } = await createOnboardingUrl({ variables: { userId: viewerUserId } });
      const url = data?.createPayoutAccountURL;
      if (!url) throw new Error('createPayoutAccountURL resolved without a URL');
      window.location.assign(url);
    } catch (err) {
      // Without this the button is inert-but-silent on the failures the api
      // actually raises here: `payout.country_unsupported`, and a profile with
      // no country at all (`createStripeAccount` refuses one). That is the same
      // dead end ENG-5708 is about, one click further along.
      const code = firstErrorCode(err);
      // A coded error is a known, already-handled condition we write copy for,
      // so `warning` — same rule as the nudge dismissal above. The default
      // `error` is kept for the ones we did not expect, which is where the
      // `resolved without a URL` throw lands.
      Sentry.captureException(err, {
        level: code === undefined ? 'error' : 'warning',
        tags: { engagement_connect_setup_failed: 'true' },
      });
      setGateError(connectSetupError(code, err));
    }
  }

  /**
   * `refetch` REJECTS under the default `errorPolicy: 'none'`, so voiding it
   * left a failed retry as an unhandled rejection with nothing shown — and
   * since `notifyOnNetworkStatusChange` is unset, `payout.loading` never flips
   * either, so the click had no visible effect at all. `retrying` is local for
   * the same reason.
   */
  async function handleRetryPayoutRead() {
    if (retrying) return;
    setGateError(null);
    setRetrying(true);
    try {
      await payout.refetch();
    } catch (err) {
      // A retry after a network failure can land on the deterministic coded
      // error instead: connectivity is back, and this expert simply has no
      // payout account. The branch below then correctly offers **Set up
      // payouts**, so saying "could not reach" underneath it would be the
      // mixed signal ENG-5708 exists to remove. The hook's error carries the
      // routing; nothing to acknowledge.
      if (hasOnlyErrorCode(err, ERROR_CODES.PAYOUT_ACCOUNT_NOT_FOUND)) return;
      // The hook already carries this error, and the banner is already up. All
      // that is missing is an acknowledgement that the click did something.
      setGateError('Still could not reach your payout account. Please try again shortly.');
    } finally {
      setRetrying(false);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} size="sm">
      <Drawer.Header onClose={onClose} breadcrumbs={[{ title: 'Make your offer' }]} />
      <Drawer.Main>
        <div className="flex flex-col gap-24">
          {/* Held back until the read has actually answered: `blocked` is true
              while in flight too, so rendering on it alone flashed a setup
              button for one frame and then swapped it for the retry (ENG-5708).
              Keyed on having an answer rather than on `!payout.loading`, which
              only looks equivalent because `notifyOnNetworkStatusChange` is
              unset — turn that on and the whole block would blink away on every
              retry. Submit is disabled throughout either way.

              `|| payoutsBlockedByServer` cannot be reached today (that flag
              needs a submit, which needs `payout.data`), but it stays: under
              `network-only` + `errorPolicy: 'none'` an errored later read
              clears `data`, which would unmount this block while `blocked`
              stays true — no banner, no button, submit still dead. */}
          {blocked && (payoutAnswered || payoutsBlockedByServer) ? (
            <div className="flex flex-col gap-12">
              <ConnectGateBanner />
              {/* Gapless inner column: the error paragraph is always mounted
                  (see below), so a container gap would leave a stray 12px
                  under the button whenever there is no error to show. */}
              <div className="flex flex-col">
                {payoutReadFailed ? (
                  <Button
                    variant="secondary"
                    onClick={() => void handleRetryPayoutRead()}
                    // `loading` and not `disabled`: disabling the control the
                    // expert just activated moves focus to `<body>` for the
                    // length of the refetch, and swapping the label changes
                    // the accessible name under them. `loading` keeps both.
                    // Re-entry is guarded in the handler instead.
                    loading={retrying}
                    aria-describedby={gateError !== null ? GATE_ERROR_ID : undefined}
                  >
                    Check again
                  </Button>
                ) : (
                  <Button
                    onClick={() => void handleConnectSetup()}
                    loading={onboarding}
                    aria-describedby={gateError !== null ? GATE_ERROR_ID : undefined}
                  >
                    Set up payouts
                  </Button>
                )}
                {/* `role="status"` and not `alert`: this appears after the
                    drawer has painted, so nothing would otherwise announce it,
                    but the banner above already owns the assertive region and
                    two of those talk over each other. Same rule as
                    `RowActions`. Mounted unconditionally with the text as the
                    conditional part — several AT/browser pairs only announce
                    mutations inside a region that was already in the tree. */}
                <p
                  role="status"
                  id={GATE_ERROR_ID}
                  className="empty:mt-0 mt-12 text-red-600 body-2 dark:text-red-400"
                >
                  {gateError}
                </p>
              </div>
            </div>
          ) : null}

          {nudgeVariant !== null ? (
            <RateNudge
              variant={nudgeVariant}
              hours={hoursValue}
              expert_rate_amount_cents={Number.isFinite(rateCents) ? rateCents : null}
              onAdjustRate={() => rateFieldRef.current?.querySelector('input')?.focus()}
              onDismiss={handleDismissNudge}
            />
          ) : null}

          <div className="flex flex-col gap-8" ref={rateFieldRef}>
            <Input
              label="Your hourly rate (USD)"
              value={rateDollars}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTouched(true);
                setRateDollars(e.target.value);
              }}
              error={touched && rateError !== null}
              helperText={touched ? (rateError ?? '') : ''}
            />
            <Checkbox
              checked={alsoUpdateProfileRate}
              onChange={() => setAlsoUpdateProfileRate((v) => !v)}
              label="Also update my profile rate"
            />
          </div>

          <div className="flex flex-col gap-8">
            <Input
              label="Not-to-exceed hours"
              value={hours}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTouched(true);
                setHours(e.target.value);
              }}
              error={touched && hoursError !== null}
              helperText={touched ? (hoursError ?? '') : ''}
            />
          </div>

          <OfferTerms value={terms} errors={termsErrors} showErrors={touched} onChange={setTerms} />

          {submitError ? <p className="text-red-600 body-2">{submitError}</p> : null}
        </div>
      </Drawer.Main>
      {/* Actions are left-aligned, not the usual bottom-right: this drawer is
          full-height and right-anchored, so a `justify-end` footer parks the
          submit button under the app's fixed support launcher (HubSpot
          Conversations via GTM, and Intercom where `VITE_INTERCOM_APP_ID` is
          set), which owns the bottom-right ~108px of the viewport at every
          breakpoint. Nothing reserves that corner globally — the only rule
          that ever did is commented out in `theme/index.css`. QA ENG-5448.  */}
      <div className="flex justify-start gap-12 border-t border-surface-border p-24">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {payout.loading ? 'Checking payouts…' : 'Make offer'}
        </Button>
      </div>
    </Drawer>
  );
}
